import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { type LayerConfig } from '../../../types/metadata';
import { fetchTableData, getTableFields } from '../services/dataTableService';
import {
  type ColumnFilter,
  type SortConfig,
  type SortDirection,
} from '../utils/tableFilterUtils';
import { useMapStore } from '../store/useMapStore';
import { ColumnFilterModal } from './ColumnFilterModal';
import './DataTableModal.css';

interface DataTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  layer: LayerConfig;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export const DataTableModal: React.FC<DataTableModalProps> = ({
  isOpen,
  onClose,
  layer,
}) => {
  // Data state
  const [pageData, setPageData] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting state (client-side on current page for responsiveness)
  const [sort, setSort] = useState<SortConfig | null>(null);

  // Store access
  const setLayerFilters = useMapStore((state) => state.setLayerFilters);
  const storedFilters = useMapStore((state) => state.layerFilters[layer.id]) || [];

  // Filter state — initialize from store
  const [filters, setFilters] = useState<ColumnFilter[]>(storedFilters);
  const [filterModalField, setFilterModalField] = useState<string | null>(null);

  // Filter to current view toggle
  const [filterToView, setFilterToView] = useState(false);
  // Snapshot of viewport bounds captured when "Viewing: Map Area" is toggled on
  const [viewBoundsSnapshot, setViewBoundsSnapshot] = useState<{ north: number; south: number; east: number; west: number } | undefined>(undefined);

  // Columns derived from schema
  const columns = useMemo(() => getTableFields(layer.schema), [layer.schema]);

  // Track request to avoid stale responses
  const requestIdRef = useRef(0);

  // Re-sync filters from store when modal opens (in case they were changed externally)
  useEffect(() => {
    if (isOpen) {
      setFilters(storedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch data whenever page, filters, rowsPerPage, sort, or filterToView changes
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentPage, rowsPerPage, filters, filterToView, sort, layer.id, layer.version]);

  /**
   * Compute current viewport bounds from viewport state.
   * Uses raw DeckGL bounds if available (most accurate), otherwise estimates from zoom/center.
   */
  const computeCurrentViewportBounds = useCallback(() => {
    // Prefer the raw DeckGL viewport bounds if available (set by MapBackground on each refresh)
    const rawBounds = useMapStore.getState().rawViewportBounds;
    if (rawBounds) {
      return rawBounds;
    }

    // Fallback: estimate from current viewport state
    const currentViewport = useMapStore.getState().viewport;
    const zoom = currentViewport.zoom;
    const worldSize = 256 * Math.pow(2, zoom);
    const lonDegreesPerPixel = 360 / worldSize;
    const latRadians = currentViewport.latitude * Math.PI / 180;
    const latDegreesPerPixel = (360 / worldSize) / Math.cos(latRadians);
    const lonOffset = (window.innerWidth / 2) * lonDegreesPerPixel;
    const latOffset = (window.innerHeight / 2) * latDegreesPerPixel;

    return {
      north: Math.min(currentViewport.latitude + latOffset, 85),
      south: Math.max(currentViewport.latitude - latOffset, -85),
      east: currentViewport.longitude + lonOffset,
      west: currentViewport.longitude - lonOffset,
    };
  }, []);

  /**
   * Handle "Viewing: Map Area" toggle.
   * When toggled ON, snapshot the current viewport bounds immediately.
   * When toggled OFF, clear the snapshot.
   */
  const handleFilterToViewToggle = useCallback(() => {
    setFilterToView(prev => {
      const next = !prev;
      if (next) {
        // Capture current viewport bounds at toggle time
        setViewBoundsSnapshot(computeCurrentViewportBounds());
      } else {
        setViewBoundsSnapshot(undefined);
      }
      return next;
    });
  }, [computeCurrentViewportBounds]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const currentRequestId = ++requestIdRef.current;
    const offset = (currentPage - 1) * rowsPerPage;

    try {
      const result = await fetchTableData({
        layer,
        fields: columns,
        filters: filters.length > 0 ? filters : undefined,
        offset,
        limit: rowsPerPage,
        sortField: sort?.field,
        sortDirection: sort?.direction || undefined,
        viewportBounds: filterToView ? viewBoundsSnapshot : undefined,
      });

      // Discard stale responses
      if (currentRequestId !== requestIdRef.current) return;

      setPageData(result.data);
      setTotalCount(result.totalCount);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error('[DataTableModal] Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Data is now sorted server-side; use pageData directly
  const sortedPageData = pageData;

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + sortedPageData.length, totalCount);

  // Reset to page 1 when filters, sort, or rowsPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, rowsPerPage, filterToView, sort]);

  // Sync filters to the store (for map data source) whenever they change
  // Skip the very first render to avoid unnecessary store writes on mount
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setLayerFilters(layer.id, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, layer.id]);

  // Column sort toggle
  const handleSort = useCallback((field: string) => {
    setSort((prev) => {
      if (prev?.field !== field) {
        return { field, direction: 'asc' };
      }
      const cycle: SortDirection[] = ['asc', 'desc', null];
      const currentIdx = cycle.indexOf(prev.direction);
      const nextDirection = cycle[(currentIdx + 1) % cycle.length];
      if (!nextDirection) return null;
      return { field, direction: nextDirection };
    });
  }, []);

  // Filter management
  const getFiltersForField = (field: string): ColumnFilter[] => {
    return filters.filter((f) => f.field === field);
  };

  const hasFilterForField = (field: string): boolean => {
    return filters.some((f) => f.field === field);
  };

  const handleFilterApply = (field: string, fieldFilters: ColumnFilter[]) => {
    setFilters((prev) => {
      const withoutField = prev.filter((f) => f.field !== field);
      return [...withoutField, ...fieldFilters];
    });
  };

  // Sort indicator
  const getSortIndicator = (field: string) => {
    if (sort?.field !== field || !sort.direction) {
      return <span className="sort-indicator inactive">⇅</span>;
    }
    return (
      <span className="sort-indicator active">
        {sort.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Get column type for display
  const getColumnType = (field: string): string => {
    const schemaEntry = layer.schema[field];
    if (!schemaEntry) return 'string';
    return typeof schemaEntry === 'object' ? schemaEntry.type : schemaEntry;
  };

  if (!isOpen) return null;

  return (
    <div className="data-table-overlay" onClick={onClose}>
      <div className="data-table-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="data-table-header">
          <div className="data-table-title">
            <h2>{layer.name} — Data Table</h2>
            <span className="data-table-count">
              {(filters.length > 0 || filterToView) && totalCount !== layer.count
                ? `${totalCount.toLocaleString()}/${layer.count.toLocaleString()} records (${layer.count > 0 ? Math.round((totalCount / layer.count) * 100) : 0}%)`
                : `${totalCount.toLocaleString()} records`}
            </span>
          </div>
          <div className="data-table-header-actions">
            <button
              className={`view-filter-toggle ${filterToView ? 'active' : ''}`}
              onClick={handleFilterToViewToggle}
              title="Filter results to current map viewport"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              {filterToView ? 'Viewing: Map Area' : 'Current View'}
            </button>
            <button className="close-btn" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Active Filters Bar */}
        {filters.length > 0 && (
          <div className="data-table-filters-bar">
            <span className="filters-label">Active Filters:</span>
            {filters.map((filter, idx) => (
              <span key={idx} className="filter-tag">
                {filter.field} {filter.operator} {filter.value}
                <button
                  className="filter-tag-remove"
                  onClick={() => {
                    setFilters((prev) => prev.filter((_, i) => i !== idx));
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            <button
              className="filters-clear-all"
              onClick={() => setFilters([])}
            >
              Clear all
            </button>
          </div>
        )}

        {/* Table Content */}
        <div className="data-table-content">
          {loading ? (
            <div className="data-table-loading">
              <div className="spinner"></div>
              <span>Loading data...</span>
            </div>
          ) : error ? (
            <div className="data-table-error">
              <p>{error}</p>
              <button onClick={fetchData}>Retry</button>
            </div>
          ) : (
            <div className="data-table-scroll-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="row-number-col">#</th>
                    {columns.map((col) => (
                      <th key={col} className="data-table-th">
                        <div className="th-content">
                          <span
                            className="th-label"
                            onClick={() => handleSort(col)}
                            title={`Sort by ${col} (${getColumnType(col)})`}
                          >
                            {col}
                            {getSortIndicator(col)}
                          </span>
                          <button
                            className={`th-filter-btn ${hasFilterForField(col) ? 'active' : ''}`}
                            onClick={() => setFilterModalField(col)}
                            title={`Filter ${col}`}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedPageData.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} className="data-table-empty">
                        {filters.length > 0 || filterToView
                          ? 'No records match the current filters.'
                          : 'No data available.'}
                      </td>
                    </tr>
                  ) : (
                    sortedPageData.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className="row-number-col">{startIndex + rowIdx + 1}</td>
                        {columns.map((col) => (
                          <td key={col} title={String(row[col] ?? '')}>
                            {formatCellValue(row[col], getColumnType(col))}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer / Pagination */}
        <div className="data-table-footer">
          <div className="data-table-page-size">
            <label>Rows per page:</label>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="data-table-page-info">
            {totalCount > 0
              ? `${(startIndex + 1).toLocaleString()}–${endIndex.toLocaleString()} of ${totalCount.toLocaleString()}`
              : '0 records'}
          </div>

          <div className="data-table-page-controls">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
              title="First page"
            >
              ««
            </button>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              title="Previous page"
            >
              «
            </button>
            <span className="page-number">
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              title="Next page"
            >
              »
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Last page"
            >
              »»
            </button>
          </div>
        </div>
      </div>

      {/* Column Filter Modal */}
      {filterModalField && (
        <ColumnFilterModal
          isOpen={!!filterModalField}
          onClose={() => setFilterModalField(null)}
          field={filterModalField}
          existingFilters={getFiltersForField(filterModalField)}
          onApply={(fieldFilters) => handleFilterApply(filterModalField, fieldFilters)}
        />
      )}
    </div>
  );
};

/**
 * Format a cell value for display
 */
function formatCellValue(value: any, type: string): string {
  if (value === null || value === undefined) return '—';

  switch (type) {
    case 'bool':
      return value ? 'true' : 'false';
    case 'timestamp': {
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    }
    case 'float':
      if (typeof value === 'number') {
        return value % 1 === 0 ? value.toString() : value.toFixed(4);
      }
      return String(value);
    default:
      return String(value);
  }
}
